import { GSection, GTooltip } from 'components/basic-blocks'
import { AppConfig } from 'config'
import { useEffect, useState } from 'react'
import { RiShareBoxFill } from 'react-icons/ri'
import { useUiStore } from 'store'
import { GRadio } from '../../../../../components/basic-blocks/g-radio'
import { PageHeader } from '../../../../../components/page-header'
import {
	conversionCode,
	conversionCodeWithMessage,
	conversionCodeWithMessageAndValue,
	conversionScript,
	trackingScript
} from '../../../../../config/tracking-code'
import { useStores } from '../../../../../hooks'
import { PageTitle } from '../../../../../utils/page-title'
import { GtmAccountSelector } from '../../gtm'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { FiCopy } from 'react-icons/fi'
import { useTrackPostHogEvent } from '../../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../../utils/posthog-events'

export const TrackingSection = () => {
	const {
		assetStore: { asset, gtmTag, getGtmTag },
		accountStore: { account, getGtmLinks }
	} = useStores()
	const isLoading = useUiStore((s) => s.isLoading)
	const trackPostHogEvent = useTrackPostHogEvent()

	const [installType, setInstallType] = useState<'GTM' | 'MANUAL'>('MANUAL')
	useEffect(() => {
		gtmTag && setInstallType('GTM')
	}, [gtmTag])

	useEffect(() => {
		if (account?.id) {
			getGtmLinks(account.id)
			asset?.id && getGtmTag(account.id, asset.id)
		}
	}, [asset?.id, account?.id])

	const isValidUrl = (urlString: string) => {
		const urlPattern = new RegExp(
			'^(https?:\\/\\/)?' + // validate protocol
				'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
				'((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
				'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
				'(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
				'(\\#[-a-z\\d_]*)?$',
			'i'
		) // validate fragment locator
		return urlPattern.test(urlString)
	}

	return (
		<div className="w-full">
			<PageTitle pageName="Tracking code" />
			<PageHeader
				title={'Tracking code'}
				subtitle={
					<p className="flex items-center text-t-checkbox-unactive">
						<span className=" font-normal">Setup tracking code on&nbsp;</span>
						<span className="font-bold max-w-[200px] truncate flex items-center">
							{asset?.name}
							{asset?.name && isValidUrl(asset.name) && (
								<a
									href={`//${asset.name}`}
									target="_blank"
									className="pl-1 pr-1 text-primary-500"
								>
									<RiShareBoxFill />
								</a>
							)}
						</span>
						<span className=" font-normal">
							&nbsp;for click behavior and conversion tracking
						</span>
					</p>
				}
			/>
			<div className="px-4 sm:px-6 md:px-8">
				<GSection
					title="Base installation"
					loading={isLoading('ASSET_GET_GTM_TAG')}
				>
					<p className="text-t-checkbox-unactive text-base">
						Setting up tracking code is required for tracking traffic and
						visitor behavior on your website.
					</p>
					<div className="my-6">
						<div className="flex items-center gap-1">
							<GRadio
								value={installType}
								checked={installType === 'GTM'}
								onChange={() => setInstallType('GTM')}
								labelClass="font-normal text-t-google-tooltip text-base"
								label="Install with Google Tag Manager"
								name="gtm"
							/>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
								content="Install TC with Clickguard with Google Tag Manager"
							>
								<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
							</GTooltip>
						</div>
						<div className="flex items-center gap-1 mt-3">
							<GRadio
								value={installType}
								checked={installType === 'MANUAL'}
								onChange={() => setInstallType('MANUAL')}
								labelClass="font-normal text-t-google-tooltip text-base"
								label="Install manually"
								name="manual"
							/>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
								content="To install manually, put the following code in the <head> section of your website"
							>
								<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
							</GTooltip>
						</div>
					</div>
					{installType === 'GTM' ? (
						<GtmAccountSelector />
					) : (
						<div>
							<p className="text-t-checkbox-unactive text-base my-2">
								To install manually, put the following code in the &lt;head&gt;
								section of your website
							</p>
							<TrackerCodeTemplate
								font="font-normal"
								isCopy={true}
								content={{
									subtitle: 'Tracking script',
									code:
										account?.id && asset?.id
											? trackingScript(account!.id, asset!.id)
											: '< code sample >'
								}}
							/>
							<p className="text-t-checkbox-unactive text-base my-2">
								Make sure this code is added to all your website pages
							</p>

							<p className="text-t-base-blue">
								Visit the{' '}
								<a
									className="mb-2 text-md text-t-email-blue hover:text-t-dark-blue font-bold"
									href={AppConfig.KNOWLEDGEBASE_URL}
									target="_blank"
									onClick={() => {
										trackPostHogEvent(
											PostHogEvent.LINK_KNOWLEDGE_BASE,
											PostHogPath.SETTINGS_TC,
											null
										)
									}}
								>
									Knowledge base
								</a>{' '}
								for additional help.
							</p>
						</div>
					)}
				</GSection>
				<GSection title="Conversion tracking">
					<p className="text-t-secondary text-base">
						Setting up conversion tracking is recommended to gain better insight
						into performance of your PPC accounts.
					</p>
					<div>
						<p className="text-t-default font-medium text-base mt-6">
							Landing page conversion
						</p>
						<p className="text-t-checkbox-unactive text-base my-2 mb-6">
							When loading a specific page should be reported as a conversion,
							like a "Thank You" page. Put the following code at the bottom of
							the page body.
						</p>
						<TrackerCodeTemplate
							isCopy={true}
							height={1}
							font="font-normal"
							content={{
								subtitle: 'Tracking script',
								code:
									account?.id && asset?.id
										? conversionScript()
										: '< code sample >'
							}}
						/>
						<p className="text-t-default font-medium text-base mt-4">
							Custom events
						</p>
						<p className="text-t-checkbox-unactive text-base mt-2 mb-6">
							When triggering a conversion on other events, like clicking on a
							button or submitting a form, you need to execute following code
							when those events happen.
						</p>
						<TrackerCodeTemplate
							isCopy={true}
							font="font-normal"
							height={9}
							content={{
								subtitle: 'Tracking script',
								code:
									account?.id && asset?.id
										? `${conversionCode()}\n${conversionCodeWithMessage()}\n${conversionCodeWithMessageAndValue()}`
										: '< code sample >'
							}}
						/>
					</div>
				</GSection>
			</div>
		</div>
	)
}

export const TrackerCodeTemplate = (props: {
	content: any
	badge?: any
	height?: number
	isCopy?: boolean
	font?: string
}) => {
	const { content, height, isCopy = true, font } = props
	const [copyStatus, setCopyStatus] = useState(false)
	return (
		<div>
			<div className="flex gap-4 mt-2">
				<div className="relative w-full">
					<textarea
						rows={height || 3}
						cols={42}
						className={`text-t-desc-gray border-t-radio border rounded-lg text-sm bg-bg-plan-fill w-full py-4
							pl-4 pr-8  resize-none placeholder-gray-500 focus:ring-0  ${
								font || 'font-medium'
							}`}
						readOnly
						value={content.code}
					/>
					{isCopy && (
						<div
							className="absolute top-3.5 right-3 cursor-pointer"
							onClick={() => copy(content.code)}
						>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none font-grotesk mb-2 bg-t-smoke min-w-max font-medium text-sm text-t-desc-gray"
								content={copyStatus ? 'Copied!' : 'Copy'}
							>
								<FiCopy className="w-5 h-5 text-t-google-tooltip hover:text-t-base-blue" />
							</GTooltip>
						</div>
					)}
				</div>
			</div>
		</div>
	)

	async function copy(text: string) {
		await navigator.clipboard.writeText(text)
		setCopyStatus(true)
		setTimeout(() => {
			setCopyStatus(false)
		}, 2000)
	}
}
